import type { ReactNode } from "react";

import { Flex } from "antd";

import { ProviderCard } from "common-services/features/shared/ProviderCard";
import { Loading } from "design-system/components/Loading/Loading";

import { CredentialsForm } from "./CredentialsForm";
import { ForgotPasswordConfirmForm } from "./ForgotPasswordConfirmForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { NewPasswordForm } from "./NewPasswordForm";

interface PasswordFlowFormsProps {
  emailHint: string;
  error: string;
  forgotConfirmUsername?: string;
  info: string;
  onBackToSignIn: () => void;
  onCancel: () => void;
  onForgotPassword: () => void;
  onResendCode: () => void;
  renderExtra?: () => ReactNode | null;
  submitCredentials: (values: { email: string; password: string }) => void;
  submitForgotConfirm: (values: { code: string; newPassword: string }) => void;
  submitForgotPassword: (values: { email: string }) => void;
  submitNewPassword: (values: { newPassword: string }) => void;
  submitting: boolean;
  viewKind: string;
}

export const PasswordFlowForms = ({
  viewKind,
  forgotConfirmUsername,
  submitting,
  error,
  info,
  emailHint,
  onCancel,
  onBackToSignIn,
  onForgotPassword,
  onResendCode,
  submitCredentials,
  submitNewPassword,
  submitForgotPassword,
  submitForgotConfirm,
  renderExtra,
}: PasswordFlowFormsProps) => {
  if (submitting) {
    return (
      <ProviderCard>
        <Flex justify="center">
          <Loading size="large" />
        </Flex>
      </ProviderCard>
    );
  }

  const renderContent = () => {
    const extra = renderExtra?.();
    if (extra) return extra;

    switch (viewKind) {
      case "newPassword": {
        return (
          <NewPasswordForm
            error={error}
            onSubmit={submitNewPassword}
            onCancel={onCancel}
          />
        );
      }

      case "forgotPassword": {
        return (
          <ForgotPasswordForm
            initialEmail={emailHint}
            error={error}
            onSubmit={submitForgotPassword}
            onBackToSignIn={onBackToSignIn}
          />
        );
      }

      case "forgotConfirm": {
        return (
          <ForgotPasswordConfirmForm
            username={forgotConfirmUsername ?? ""}
            error={error}
            onSubmit={submitForgotConfirm}
            onResendCode={onResendCode}
          />
        );
      }

      default: {
        return (
          <CredentialsForm
            initialEmail={emailHint}
            error={error}
            info={info}
            onSubmit={submitCredentials}
            onForgotPassword={onForgotPassword}
            onCancel={onCancel}
          />
        );
      }
    }
  };

  return <ProviderCard>{renderContent()}</ProviderCard>;
};
